import React from "react";
import { dateRange } from "./templateHelpers";

/** Shared renderer for the newer resume sections (Projects, Internships, Certifications,
 * Languages, Websites, Hobbies, Extra-Curricular, Custom) — reused across 19 of the 22 visual
 * templates (Modern/Classic/Minimal render these inline themselves — see SingleColumnTemplate.js
 * — so they can fully honor the learner's custom drag-and-drop section order).
 * variant="boxed" matches templates that render sections inside a bordered box
 * (CompactGridTemplate, MonogramTemplate); default "plain" matches the rest. */
export default function ExtraResumeSections({ draft, accent, variant = "plain" }) {
  const heading = variant === "boxed" ? "text-[11px] font-bold uppercase tracking-wider mb-1" : "text-xs font-bold uppercase tracking-wider";
  const Wrap = ({ children }) =>
    variant === "boxed"
      ? <div className="mt-3 border rounded-lg p-3" style={{ borderColor: `${accent}25` }}>{children}</div>
      : <div className="mt-4">{children}</div>;

  return (
    <>
      {draft.projects?.length > 0 && (
        <Wrap>
          <h2 className={heading} style={{ color: accent }}>Projects</h2>
          {draft.projects.map((p, i) => (
            <div key={i} className="mt-1.5">
              <p className="font-semibold text-slate-800">{p.title}{p.tech_stack ? ` — ${p.tech_stack}` : ""}</p>
              {(p.bullets || []).filter(Boolean).length > 0 && (
                <ul className="list-disc list-inside text-slate-700 mt-0.5">{p.bullets.filter(Boolean).map((b, bi) => <li key={bi}>{b}</li>)}</ul>
              )}
            </div>
          ))}
        </Wrap>
      )}

      {draft.internships?.length > 0 && (
        <Wrap>
          <h2 className={heading} style={{ color: accent }}>Internships</h2>
          {draft.internships.map((it, i) => (
            <div key={i} className="mt-1.5">
              <p className="font-semibold text-slate-800">{it.title} — {it.company} <span className="text-slate-400 font-normal italic">({dateRange(it.start_date, it.end_date, it.current)})</span></p>
              <ul className="list-disc list-inside text-slate-700 mt-0.5">{(it.bullets || []).filter(Boolean).map((b, bi) => <li key={bi}>{b}</li>)}</ul>
            </div>
          ))}
        </Wrap>
      )}

      {draft.certifications?.length > 0 && (
        <Wrap>
          <h2 className={heading} style={{ color: accent }}>Certifications</h2>
          {draft.certifications.map((c, i) => (
            <p key={i} className="text-slate-700 mt-1">{c.name}{c.issuer ? ` — ${c.issuer}` : ""} {c.date && <span className="text-slate-400 italic">({c.date})</span>}</p>
          ))}
        </Wrap>
      )}

      {draft.languages?.length > 0 && (
        <Wrap>
          <h2 className={heading} style={{ color: accent }}>Languages</h2>
          <p className="text-slate-700 mt-1">{draft.languages.map((l) => `${l.name} (${l.proficiency})`).join("  •  ")}</p>
        </Wrap>
      )}

      {draft.websites?.length > 0 && (
        <Wrap>
          <h2 className={heading} style={{ color: accent }}>Websites & Social</h2>
          <p className="text-slate-700 mt-1">{draft.websites.map((w) => w.label || w.url).join("  •  ")}</p>
        </Wrap>
      )}

      {draft.hobbies?.length > 0 && (
        <Wrap>
          <h2 className={heading} style={{ color: accent }}>Hobbies</h2>
          <p className="text-slate-700 mt-1">{draft.hobbies.join("  •  ")}</p>
        </Wrap>
      )}

      {draft.extracurricular?.length > 0 && (
        <Wrap>
          <h2 className={heading} style={{ color: accent }}>Extra-Curricular Activities</h2>
          <p className="text-slate-700 mt-1">{draft.extracurricular.join("  •  ")}</p>
        </Wrap>
      )}

      {(draft.custom_sections || []).filter((cs) => cs.title || cs.items?.length).map((cs, ci) => (
        <Wrap key={ci}>
          <h2 className={heading} style={{ color: accent }}>{cs.title || "Additional Section"}</h2>
          {cs.items?.filter(Boolean).length > 0 && (
            <ul className="list-disc list-inside text-slate-700 mt-0.5">{cs.items.filter(Boolean).map((it, ii) => <li key={ii}>{it}</li>)}</ul>
          )}
        </Wrap>
      ))}
    </>
  );
}
